"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { MapPin, ShieldCheck, Wrench, Package, ArrowUpRight } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Link } from "@/components/common/Link";
const depotMetrics = [{
  label: "CATALOG BREADTH",
  value: "33 Units",
  sub: "Across 11 Categories"
}, {
  label: "DISTRIBUTION",
  value: "50 States",
  sub: "Nationwide Logistics"
}, {
  label: "TIER COVERAGE",
  value: "Entry to Pro",
  sub: "Dual Spec Tiers"
}];
export default function Story() {
  const shouldReduceMotion = useReducedMotion();
  const entranceVariant = {
    hidden: {
      opacity: 0,
      y: shouldReduceMotion ? 0 : 14
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: shouldReduceMotion ? 0 : 0.24,
        ease: [0.16, 1, 0.3, 1]
      }
    }
  };
  return <section id="our-story" data-nav="dark" className="bg-background text-foreground py-16 md:py-24 border-b border-border">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-start">
          {/* Left Column: Story Content */}
          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true,
          amount: 0.2
        }} variants={entranceVariant} className="lg:col-span-7 flex flex-col justify-between">
            <div>
              {/* Technical Eyebrow */}
              <div className="flex items-center gap-2 mb-4">
                <Badge variant="outline" className="rounded-xs border-border bg-card/60 px-2.5 py-1 text-[11px] font-mono tracking-widest text-muted-foreground uppercase">
                  EST. 2024 // DEPOT OPERATIONS
                </Badge>
              </div>

              {/* Section Headline with Precision Spec Rule */}
              <div className="border-l-[3px] border-primary pl-4 md:pl-5 mb-6">
                <h2 className="text-3xl sm:text-4xl lg:text-5xl font-serif font-bold uppercase tracking-tight text-foreground leading-[1.1]">
                  Built for heavy duty workshops and exact tolerances
                </h2>
              </div>

              {/* Body Copy */}
              <div className="space-y-4 text-base sm:text-lg text-muted-foreground font-sans leading-relaxed mb-8">
                <p>
                  Huga Scalet LLC distributes professional-grade tools, workshop hardware,
                  garage machinery, and maintenance gear across the United States. Founded in Orange,
                  California, we bridge the gap between heavy industrial fabrication needs and direct shop-floor delivery.
                </p>
                <p>
                  Every piece in our 33-product catalog is chosen for dimensional accuracy, metallurgical
                  durability, and reliable service life under daily mechanical strain. From introductory setup
                  stations to high-capacity industrial machines, we give builders and technicians equipment they can trust.
                </p>
              </div>

              {/* Specification Callout Card */}
              <Card className="rounded-xs border border-border bg-card shadow-sm transition-[border-color,box-shadow] duration-200 hover:border-primary/50 mb-8">
                <CardContent className="p-5 sm:p-6 space-y-5">
                  <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 border-b border-border pb-4">
                    <div>
                      <span className="text-[11px] font-mono uppercase tracking-wider text-muted-foreground block">
                        REGISTERED ENTITY
                      </span>
                      <span className="text-base font-semibold text-foreground font-serif uppercase tracking-wide">
                        Huga Scalet LLC
                      </span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="inline-flex items-center gap-1 text-xs font-mono text-primary bg-primary/10 border border-primary/20 px-2 py-1 rounded-xs">
                        <ShieldCheck className="w-3.5 h-3.5" />
                        VERIFIED FACILITY
                      </span>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div className="flex items-start gap-3">
                      <div className="p-2 rounded-xs bg-muted border border-border shrink-0 mt-0.5">
                        <MapPin className="w-4 h-4 text-primary" />
                      </div>
                      <div>
                        <span className="text-xs font-mono text-muted-foreground uppercase block">
                          PHYSICAL DEPOT
                        </span>
                        <p className="text-sm font-sans text-foreground leading-snug">
                          2000 W Palmyra Ave, Ste 28, Orange, CA 92868, USA
                        </p>
                      </div>
                    </div>

                    <div className="flex items-start gap-3">
                      <div className="p-2 rounded-xs bg-muted border border-border shrink-0 mt-0.5">
                        <Wrench className="w-4 h-4 text-primary" />
                      </div>
                      <div>
                        <span className="text-xs font-mono text-muted-foreground uppercase block">
                          SERVICE SCOPE
                        </span>
                        <p className="text-sm font-sans text-foreground leading-snug">
                          Tools, Workshop, Garage & Maintenance
                        </p>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Metrics Grid */}
              <div className="grid grid-cols-3 gap-3 border-t border-border pt-6 mb-8">
                {depotMetrics.map((metric, i) => <div key={metric.label} data-index={i} className="space-y-1">
                    <span className="text-[10px] sm:text-[11px] font-mono text-muted-foreground tracking-wider uppercase block">
                      {metric.label}
                    </span>
                    <span className="text-lg sm:text-xl font-serif font-bold text-foreground block">
                      {metric.value}
                    </span>
                    <span className="text-xs text-muted-foreground hidden sm:block">
                      {metric.sub}
                    </span>
                  </div>)}
              </div>

              {/* Action Link */}
              <div className="flex flex-wrap items-center gap-4">
                <Button asChild className="rounded-xs bg-primary text-primary-foreground font-serif font-bold uppercase tracking-wider hover:bg-primary/90 px-6 py-2.5 h-auto text-sm">
                  <Link to="/ShopPage">
                    View Catalog
                    <ArrowUpRight className="w-4 h-4 ml-1.5" />
                  </Link>
                </Button>
                <Button asChild variant="outline" className="rounded-xs border-border bg-secondary text-secondary-foreground hover:bg-accent font-serif uppercase tracking-wider text-sm px-5 py-2.5 h-auto">
                  <Link to="/ContactPage">
                    Contact Operations
                  </Link>
                </Button>
              </div>
            </div>
          </motion.div>

          {/* Right Column: Industrial Workshop Visual */}
          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true,
          amount: 0.2
        }} variants={entranceVariant} className="lg:col-span-5 relative">
            <div className="relative rounded-xs border border-border bg-card overflow-hidden shadow-md">
              <div className="aspect-4/5 sm:aspect-3/4 lg:aspect-4/5 w-full relative">
                <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1787497/img/workshop_depot_operations.webp" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1787497/img/workshop_depot_operations.webp-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1787497/img/workshop_depot_operations.webp-wvc-sizes" alt="Industrial tool testing and equipment staging at Huga Scalet depot" loading="lazy" decoding="async" className="w-full h-full object-cover object-center grayscale contrast-125 hover:grayscale-0 transition-[filter] duration-500" />
                <div className="absolute inset-0 bg-gradient-to-t from-background/90 via-background/20 to-transparent pointer-events-none" />
              </div>

              {/* Corner Spec Badge Overlay */}
              <div className="absolute top-4 right-4 bg-background/90 backdrop-blur-xs border border-border px-3 py-1.5 rounded-xs">
                <div className="flex items-center gap-2">
                  <div className="w-2 h-2 rounded-full bg-primary animate-pulse" />
                  <span className="text-[11px] font-mono tracking-wider uppercase text-foreground">
                    ORANGE, CA HUB
                  </span>
                </div>
              </div>

              {/* Bottom Card Summary Info */}
              <div className="absolute bottom-0 inset-x-0 p-5 bg-card/95 border-t border-border backdrop-blur-xs">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2 text-xs font-mono text-muted-foreground">
                    <Package className="w-4 h-4 text-primary" />
                    <span>NATIONWIDE FREIGHT</span>
                  </div>
                  <span className="text-xs font-mono text-muted-foreground">
                    SPEC // US-2024
                  </span>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>;
}